import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
export type MarineDashboardFilterOptions = {
  dealNames: string[];
  vesselTypes: string[];
};

const FILTER_OPTIONS_PATH = 'marine-dashboard/filter-options';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

export type MarineTapeDashboardKpiLatest = {
  asOfDateIso: string;
  totalVessels: number;
  collateralValue: number;
  onCharterCount: number;
  delinquentCount: number;
};

function parseNum(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseMarineKpiRow(raw: unknown): MarineTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDateIso ?? o.AsOfDateIso;
  if (typeof asOf !== 'string' || asOf.length === 0) return null;
  const totalVessels = parseNum(o.totalVessels ?? o.TotalVessels);
  const collateralValue = parseNum(o.collateralValue ?? o.CollateralValue);
  if (totalVessels == null || collateralValue == null) return null;
  const onCharterCount = parseNum(o.onCharterCount ?? o.OnCharterCount);
  const delinquentCount = parseNum(o.delinquentCount ?? o.DelinquentCount);
  if (onCharterCount == null || delinquentCount == null) return null;

  return {
    asOfDateIso: asOf,
    totalVessels: Math.trunc(totalVessels),
    collateralValue,
    onCharterCount: Math.trunc(onCharterCount),
    delinquentCount: Math.trunc(delinquentCount),
  };
}

/**
 * Snapshot KPIs for SP_MARINE_LOAN_TAPE, optionally filtered by deal, vessel type, and as-of date.
 */

export type MarineDashboardFilters = {
  dealName: readonly string[];
  vesselType: readonly string[];
  /** Selected tape AS_OF_DATE (YYYY-MM-DD). Empty = latest. */
  asOfDate?: string;
};

function appendMarineDashboardFilterParams(qs: URLSearchParams, filters: MarineDashboardFilters) {
  for (const v of filters.dealName) {
    const t = v.trim();
    if (t) qs.append('dealName', t);
  }
  for (const v of filters.vesselType) {
    const t = v.trim();
    if (t) qs.append('vesselType', t);
  }
  const asOf = filters.asOfDate?.trim();
  if (asOf) qs.set('asOfDate', asOf);
}

export type MarineCharterExpirationForecastRow = {
  forecastMonthIso: string;
  activeVesselCount: number;
  endedVesselCount: number;
  expiringInMonthCount: number;
};

function parseMarineCharterExpirationForecastRows(raw: unknown): MarineCharterExpirationForecastRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) return [];
  const out: MarineCharterExpirationForecastRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const iso = r.forecastMonthIso ?? r.ForecastMonthIso;
    if (typeof iso !== 'string' || iso.length === 0) continue;
    const active = parseNum(r.activeVesselCount ?? r.ActiveVesselCount);
    const ended = parseNum(r.endedVesselCount ?? r.EndedVesselCount);
    const expiring = parseNum(r.expiringInMonthCount ?? r.ExpiringInMonthCount);
    if (active == null || ended == null || expiring == null) continue;
    out.push({
      forecastMonthIso: iso,
      activeVesselCount: Math.trunc(active),
      endedVesselCount: Math.trunc(ended),
      expiringInMonthCount: Math.trunc(expiring),
    });
  }
  return out;
}

/**
 * 12-month charter roll from the latest filtered tape (active line + expiring / ended bars), same filters as KPIs.
 */

export type MarineRemainingCharterTermBucketRow = {
  remainingTermBucket: string;
  vesselCount: number;
};

function parseMarineRemainingCharterTermBucketRows(raw: unknown): MarineRemainingCharterTermBucketRow[] {
  if (raw == null || typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) return [];
  const out: MarineRemainingCharterTermBucketRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const bucket = r.remainingTermBucket ?? r.RemainingTermBucket;
    if (typeof bucket !== 'string' || bucket.length === 0) continue;
    const vesselCount = parseNum(r.vesselCount ?? r.VesselCount);
    if (vesselCount == null) continue;
    out.push({
      remainingTermBucket: bucket,
      vesselCount: Math.trunc(vesselCount),
    });
  }
  return out;
}

/** Latest-tape distinct vessel counts by remaining charter term bucket (same filters as KPIs). */

export class MarineDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
    return {};
  };

  fetchMarineDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
      vesselTypes: stringArrayFromBody(body, 'vesselTypes', 'VesselTypes'),
    };
  };

  fetchMarineDashboardAsOfDates = async () => {
    const body = await this.getJsonBody('marine-dashboard/as-of-dates');
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchMarineTapeDashboardKpi = async (filters: MarineDashboardFilters) => {
    const qs = new URLSearchParams();
    appendMarineDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path = q.length > 0 ? `marine-dashboard/tape-kpi?${q}` : 'marine-dashboard/tape-kpi';

    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) return null;
      const parsed: unknown = JSON.parse(text) as unknown;
      return parseMarineKpiRow(parsed);
    }
    return parseMarineKpiRow(raw);
  };

  fetchMarineCharterExpirationForecast = async (filters: MarineDashboardFilters) => {
    const qs = new URLSearchParams();
    appendMarineDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0
        ? `marine-dashboard/charter-expiration-forecast?${q}`
        : 'marine-dashboard/charter-expiration-forecast';

    const body = await this.getJsonBody(path);
    return parseMarineCharterExpirationForecastRows(body);
  };

  fetchMarineRemainingCharterTermBuckets = async (filters: MarineDashboardFilters) => {
    const qs = new URLSearchParams();
    appendMarineDashboardFilterParams(qs, filters);
    const q = qs.toString();
    const path =
      q.length > 0
        ? `marine-dashboard/remaining-charter-term-buckets?${q}`
        : 'marine-dashboard/remaining-charter-term-buckets';

    const body = await this.getJsonBody(path);
    return parseMarineRemainingCharterTermBucketRows(body);
  };
}
